// Imports => React
import React, {useRef, useMemo} from 'react';
import {Props} from './ac-button.proptypes';
import clsx from 'clsx';

// Imports => Constants
import {THEMES, VARIANTS} from '@asdf-constants';

// Imports => Utilities
import {AcUUID, AcGenerateThemeClassNames} from '@asdf-utils';

// Imports => Atoms
import {AcRipple} from '@asdf-atoms/ac-ripple';

const _CLASSES = {
  MAIN: 'ac-button',
  ALT: 'ac-button--alt',
  SIZES: {
    DEFAULT: 'ac-button--size-regular',
    SMALL: 'ac-button--size-small',
    MEDIUM: 'ac-button--size-medium',
    LARGE: 'ac-button--size-large',
  },
  VARIANT: {
    TRANSPARENT: 'ac-button--ghost', // backward compatibility
    TEXT: 'ac-button--ghost', // backward compatibility
    GHOST: 'ac-button--ghost',
    OUTLINE: 'ac-button--outline',
    FILL: 'ac-button--fill',
  },
  THEME: AcGenerateThemeClassNames('ac-button'),
  BLOCK: 'ac-button--block',
  DISABLED: 'ac-button--disabled',
  LOADING: 'ac-button--loading',
  LOADER: 'ac-button__loader',
};

// Component
const AcButton = ({
  children,
  id = AcUUID(),
  tag = 'button',
  type = 'button',
  theme = 'default',
  variant = 'fill',
  size = 'default',
  disabled = false,
  block = false,
  loading = false,
  callback,
  className,
  ...rest
}) => {
  const $element = useRef();

  const handleClick = event => {
    if (!disabled && callback) callback(event, $element.current);
  };

  const getLoaderClassNames = useMemo(() => {
    return clsx(_CLASSES.LOADER);
  }, []);

  const getStyleClassNames = useMemo(() => {
    return clsx(
      _CLASSES.MAIN,
      theme && _CLASSES.THEME[theme.toUpperCase()],
      variant && _CLASSES.VARIANT[variant.toUpperCase()],
      size && _CLASSES.SIZES[size.toUpperCase()],
      disabled && _CLASSES.DISABLED,
      block && _CLASSES.BLOCK,
      loading && _CLASSES.LOADING,
      className
    );
  }, [size, disabled, block, theme, variant, loading, className]);

  const Tag = tag;

  const renderRipple = useMemo(() => {
    let ripple_theme;

    if ([THEMES.WHITE, THEMES.LIGHT, THEMES.SUBTLE].indexOf(theme) > -1)
      ripple_theme = THEMES.PITCH;
    else if (
      [THEMES.OMEGA, THEMES.OMEGA_LIGHT].indexOf(theme) > -1 &&
      [VARIANTS.TRANSPARENT, VARIANTS.TEXT, VARIANTS.GHOST, VARIANTS.OUTLINE].indexOf(variant) > -1
    )
      ripple_theme = THEMES.OMEGA;
    else if (
      [THEMES.ALPHA, THEMES.ALPHA_LIGHT].indexOf(theme) > -1 &&
      [VARIANTS.TRANSPARENT, VARIANTS.TEXT, VARIANTS.GHOST, VARIANTS.OUTLINE].indexOf(variant) > -1
    )
      ripple_theme = THEMES.ALPHA;
    else if (
      [THEMES.ALPHA, THEMES.ALPHA_LIGHT, THEMES.OMEGA, THEMES.OMEGA_LIGHT].indexOf(theme) > -1
    )
      ripple_theme = THEMES.PITCH;
    else if (
      [VARIANTS.TRANSPARENT, VARIANTS.TEXT, VARIANTS.GHOST, VARIANTS.OUTLINE].indexOf(variant) > -1
    )
      ripple_theme = THEMES.PITCH;

    return <AcRipple theme={ripple_theme} size={'small'} simple />;
  }, [theme, variant]);

  return (
    <Tag
      ref={$element}
      id={id}
      type={type}
      aria-roledescription={'button'}
      disabled={disabled}
      onClick={handleClick}
      className={getStyleClassNames}
      {...rest}
    >
      <div className={getLoaderClassNames} />
      {children || 'Button'}
      {renderRipple}
    </Tag>
  );
};

AcButton.displayName = 'AcButton';
AcButton.propTypes = Props;

export {AcButton};
export default AcButton;
